
import { useCallback } from "react";
import { logger } from "@/lib/logger";

type ConnectFunction = (
  webhookUrl: string,
  slackToken: string,
  saveToDatabase: boolean,
  triggerTag: string,
  isFileProcessingEnabled: boolean,
  isAutoResponsesEnabled: boolean
) => Promise<boolean>;

type DisconnectFunction = () => Promise<boolean>;

type ResetCredentialsFunction = () => void;

/**
 * Hook for Slack connection actions
 */
export const useConnectionActions = (
  aiStaffId: string,
  handleConnect: ConnectFunction,
  handleDisconnect: DisconnectFunction,
  resetCredentials: ResetCredentialsFunction
) => {
  const onConnect = useCallback(async () => {
    try {
      // These will be provided from the main hook
      const webhookUrl = "";
      const slackToken = "";
      const triggerTag = "@marketing";
      const isFileProcessingEnabled = true;
      const isAutoResponsesEnabled = true;
      
      return await handleConnect(
        webhookUrl,
        slackToken,
        true,
        triggerTag,
        isFileProcessingEnabled,
        isAutoResponsesEnabled
      );
    } catch (error) {
      logger.error({
        message: "Error in onConnect handler",
        error: error instanceof Error ? error : new Error(String(error))
      });
      return false;
    }
  }, [handleConnect]);

  const onDisconnect = useCallback(async () => {
    try {
      const success = await handleDisconnect();
      
      if (success) {
        resetCredentials();
      }
      
      return success;
    } catch (error) {
      logger.error({
        message: "Error in onDisconnect handler",
        error: error instanceof Error ? error : new Error(String(error))
      });
      return false;
    }
  }, [handleDisconnect, resetCredentials]);

  return {
    onConnect,
    onDisconnect
  };
};
